"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
export default function ServiceAvailabilityFAQ() {
  const {
    ref: preambleRef,
    isVisible: preambleVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: accordionRef,
    isVisible: accordionVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const protocols = [{
    number: "01",
    question: "What is supervised parenting time?",
    paragraphs: ["Supervised parenting time provides a safe, neutral environment where children can maintain or build relationships with a parent while ensuring their physical and emotional safety.", "Our professional monitors remain present throughout the entire session, observing interactions and ensuring that all court-ordered guidelines are followed with firm, compassionate boundaries."]
  }, {
    number: "02",
    question: "How do monitored exchanges work?",
    paragraphs: ["Monitored exchanges are designed to minimize conflict between parents during the transfer of a child. We provide a neutral, structured location to ensure the transition is calm and child-centered.", "Our staff facilitates the hand-off, documenting the time and condition of the child, which helps reduce stress for both the parents and the child involved."]
  }, {
    number: "03",
    question: "Do you provide court reporting?",
    paragraphs: ["Yes, we provide objective, factual observation reports for the court. These reports document the interactions observed during supervised sessions without offering clinical opinions or subjective interpretations.", "Our goal is to provide the court with clear, neutral information that supports the best interests of the child and the family's ongoing legal process."]
  }, {
    number: "04",
    question: "What is your approach to safety?",
    paragraphs: ["Safety is our primary directive. We utilize trauma-informed practices to ensure that every session is structured to prevent conflict and prioritize the child's sense of security.", "Our monitors are trained to de-escalate situations, maintain firm boundaries, and intervene immediately if any behavior deviates from the established safety plan."]
  }, {
    number: "05",
    question: "How do I begin the intake process?",
    paragraphs: ["The intake process begins with a consultation to understand your specific court orders and family needs. We require all relevant legal documentation before scheduling the first session.", "Once documentation is reviewed and the intake fee is processed, we will coordinate with all parties to establish a consistent, reliable schedule for your services."]
  }, {
    number: "06",
    question: "Are your services available statewide?",
    paragraphs: ["Tiny to Teen Supervised Parenting & Education Services operates throughout Arizona. We offer both facility-based services and community-based support depending on the specific requirements of your court order.", "Please contact our office to discuss your location and specific needs, as we strive to provide accessible support for families navigating complex legal situations."]
  }, {
    number: "07",
    question: "What should I bring to a session?",
    paragraphs: ["For supervised parenting time, parents are encouraged to bring age-appropriate activities, books, or games that facilitate positive interaction.", "Please ensure all items are approved in advance. We ask that you arrive promptly and remain focused on the child's needs throughout the duration of the visit."]
  }, {
    number: "08",
    question: "Are visits confidential?",
    paragraphs: ["Confidential, but not privileged. Here's what that means for you.", "Everything about your visits stays private. We don't talk about our clients, we don't share your information around, and we never hand our reports or notes to individuals. Reports go directly to the court.", "But we are not legally privileged, and we won't pretend otherwise. Legal privilege, the kind that protects what you say from ever being disclosed, belongs to a priest, a lawyer, a doctor, a mental health practitioner, and a spouse. We are none of those. Our notes and reports can be subpoenaed, and like everyone who works with children, we are mandated reporters.", "We would rather you hear that plainly from us at the start than be surprised by it later."]
  }];
  return <section data-section-id="1615" id="serviceavailabilityfaq" className="relative w-full bg-background py-32 md:py-44 overflow-hidden">
      <Separator className="absolute top-0 left-0 right-0 bg-border" />

      <div className="mx-auto max-w-[1440px] px-6 md:px-16 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8">
          <div ref={preambleRef} className="lg:col-span-5 relative">
            <div className="lg:sticky lg:top-32">
              <div className="relative">
                <span aria-hidden="true" className="pointer-events-none absolute -right-4 -top-12 md:-top-20 font-default font-thin leading-none select-none text-border/30" style={{
                fontSize: "clamp(10rem, 22vw, 22rem)",
                fontWeight: 100
              }}>
                  06
                </span>

                <div className={`relative font-sans uppercase text-foreground transition-all duration-700 ease-out ${preambleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
                letterSpacing: "0.18em"
              }}>
                  N° 06 — SERVICE FAQ
                </div>

                <h2 className={`relative font-default text-foreground mt-12 md:mt-14 transition-all duration-700 ease-out delay-[80ms] ${preambleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                fontSize: "clamp(2.25rem, 4.8vw, 4.75rem)",
                fontWeight: 250,
                letterSpacing: "-0.02em",
                lineHeight: 1.02
              }}>
                  Understanding our <em className="italic font-light">professional</em> services.
                </h2>

                <p className={`relative font-default text-foreground/80 mt-10 max-w-md transition-all duration-700 ease-out delay-[160ms] ${preambleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)",
                fontWeight: 300,
                letterSpacing: "-0.005em",
                lineHeight: 1.55
              }}>
                  Tiny to Teen Supervised Parenting & Education Services provides neutral, structured support for families in Arizona. These protocols ensure clarity, safety, and consistency for all parties involved.
                </p>

                <div className={`relative mt-12 font-sans uppercase text-foreground/60 transition-all duration-700 ease-out delay-[240ms] ${preambleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                fontSize: "clamp(0.6875rem, 0.75vw, 0.75rem)",
                letterSpacing: "0.08em"
              }}>
                  UPDATED · 2024
                </div>
              </div>
            </div>
          </div>

          <div ref={accordionRef} className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full border-t border-border">
              {protocols.map((item, i) => <AccordionItem data-index={i} key={item.number} value={`item-${item.number}`} className={`border-b border-border last:border-b-0 transition-all duration-700 ease-out ${accordionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionDelay: `${i * 60}ms`
            }}>
                  <AccordionTrigger className="group py-8 hover:no-underline rounded-md data-[state=open]:[&_.indicator]:text-secondary">
                    <div className="flex-1 grid grid-cols-12 items-baseline gap-4 pr-4">
                      <div className="col-span-9 md:col-span-9 text-left">
                        <h4 className="font-default text-foreground" style={{
                      fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
                      fontWeight: 300,
                      letterSpacing: "0em",
                      lineHeight: 1.3
                    }}>
                          {item.question}
                        </h4>
                      </div>
                      <div className="hidden md:block md:col-span-2 font-sans text-foreground/60" style={{
                    fontSize: "clamp(0.6875rem, 0.75vw, 0.75rem)",
                    letterSpacing: "0.08em"
                  }}>
                        {item.number}
                      </div>
                      <div className="col-span-3 md:col-span-1 text-right font-sans text-foreground indicator transition-colors duration-300" style={{
                    fontSize: "clamp(0.875rem, 1vw, 1rem)",
                    letterSpacing: "0.05em"
                  }}>
                        <span className="group-data-[state=open]:hidden">+</span>
                        <span className="hidden group-data-[state=open]:inline">
                          —
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-0 pb-0">
                    <div className="bg-card py-10 px-0">
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-10 md:col-start-1 space-y-5">
                          {item.paragraphs.map((p, pi) => <p data-index={pi} key={pi} className="font-default text-foreground" style={{
                        fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                        fontWeight: 400,
                        lineHeight: 1.65
                      }}>
                              {p}
                            </p>)}

                          <Separator className="bg-border mt-8" />

                          <Link to="/ContactPage" className="inline-block mt-2 font-sans uppercase text-foreground hover:text-secondary transition-colors duration-220" style={{
                        fontSize: "clamp(0.6875rem, 0.75vw, 0.75rem)",
                        letterSpacing: "0.08em",
                        textDecoration: "underline",
                        textUnderlineOffset: "6px"
                      }}>
                            Contact Our Team — Inquiries
                          </Link>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>

      <Separator className="absolute bottom-0 left-0 right-0 bg-border" />
    </section>;
}